/** One batch's place in the apply order. */
export interface HsBatchTicket {
  readonly index: number;
  /** Settles when every earlier batch has finished applying (or failed). */
  readonly previous: Promise<void>;
  /** Lets the next batch apply; call it when this batch is done, also on failure. */
  finish(): void;
}

/**
 * Applies batches in the order they were started while their downloads run in parallel. After a batch
 * fails, later batches are canceled so the scene never shows a gap in the middle of the load.
 */
export class HsOrderedBatches {
  private chain: Promise<void> = Promise.resolve();
  private nextIndex: number = 0;
  private firstFailedIndex: number = Infinity;

  /** Takes the next place in the order; call before the batch's download starts. */
  public begin(): HsBatchTicket {
    const previous: Promise<void> = this.chain;
    let finish: () => void = () => undefined;
    this.chain = new Promise<void>((resolve: () => void) => {
      finish = resolve;
    });
    return { index: this.nextIndex++, previous, finish: () => finish() };
  }

  /** True when an earlier (or this) batch failed, so this one must not apply. */
  public canceled(ticket: HsBatchTicket): boolean {
    return ticket.index >= this.firstFailedIndex;
  }

  public fail(ticket: HsBatchTicket): void {
    this.firstFailedIndex = Math.min(this.firstFailedIndex, ticket.index);
  }
}
